// ** React Imports **
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// ** React-Hook-Form **
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// ** Firebase Imports **
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { authentication } from "src/configs/firebase-config";

// ** Redux **
import { useDispatch, useSelector } from "react-redux";
import { register as registerAction, login } from "src/redux/actions/AuthAction";
import authConfig from "../../../configs/auth";

// ** Snackbar **
import { useSnackbar } from 'notistack';

// ** Constants **
import { FormValidationMessages } from "src/constants/form.const";

// ** API **
import { useQuery } from "react-query";
import { ApiEndPoints } from "src/network/endpoints";
import { axiosInstance } from "src/network/adapter";

// ** Material-UI **
import { Box, Chip, Stack, Skeleton } from "@mui/material";

// ** Components **
import InputField from '../../../components/authentication/InputField';
import LoadingOverlay from '../../../components/authentication/LoadingOverlay';

// ** Hooks **
import { useDoubleTickWidget } from "src/lib/useDoubleTickWidget";

// ** Styles **
import './RegisterStepAccount.css';

/**
 * RegisterStepAccount Component (Step 0)
 * 
 * Account creation step - first step in the registration flow
 * Users can create account via:
 * - Google SSO
 * - Microsoft SSO
 * - Manual registration form
 * 
 * After successful account creation, does NOT redirect to home
 * Instead, calls onComplete to move to next step (Business Registration)
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onComplete - Callback when account created successfully
 */

// Validation schema for manual registration
const validationSchema = yup.object().shape({
  fullName: yup
    .string()
    .trim()
    .min(2, FormValidationMessages.FULLNAME?.minmax || "full name must be between 2-30 characters")
    .max(30, FormValidationMessages.FULLNAME?.minmax || "full name must be between 2-30 characters")
    .matches(
      /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
      FormValidationMessages.FULLNAME?.onlyLetters || "full name can only contain letters"
    )
    .required(FormValidationMessages.FULLNAME?.required || "full name is required"),
  userName: yup
    .string()
    .min(2, "username must be between 2-50 characters")
    .max(50, "username must be between 2-50 characters")
    .required("username is required"),
  email: yup
    .string()
    .email(FormValidationMessages.EMAIL.invalid)
    .required(FormValidationMessages.EMAIL.required),
  password: yup
    .string()
    .min(6, FormValidationMessages.PASSWORD?.minmax || "password must be between 6-12 characters")
    .max(12, FormValidationMessages.PASSWORD?.minmax || "password must be between 6-12 characters")
    .required(FormValidationMessages.PASSWORD.required),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], FormValidationMessages.CONFIRM_PASSWORD?.match || "passwords must match")
    .required(FormValidationMessages.CONFIRM_PASSWORD?.required || "confirm password is required"),
});

const RegisterStepAccount = ({ onComplete }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loadingBtnLoader } = useSelector((state) => state.auth);

  // Initialize chat widget
  useDoubleTickWidget();

  // ** State **
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [debouncedUsername, setDebouncedUsername] = useState("");

  // ** React Hook Form **
  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      fullName: "",
      userName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  const formValues = watch();

  // ** Debounce username input
  const debounceInput = useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedUsername(value);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // ** Handle username input change
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    setValue("userName", value);
    debounceInput(value);
  };

  // ** Fetch username suggestions
  const {
    data: userNameSuggestion,
    refetch: fetchAgain,
    isLoading: isLoadingSuggestions,
  } = useQuery(
    ["userProfileSetting", debouncedUsername],
    async ({ signal }) => {
      if (debouncedUsername.length < 1) return null;
      const response = await axiosInstance.get(
        ApiEndPoints.AUTH.fetchUsers(debouncedUsername),
        { signal }
      );
      return response.data?.suggestion || [];
    },
    {
      enabled: !!debouncedUsername && debouncedUsername.length >= 1,
      refetchOnWindowFocus: false,
    }
  );

  // ** Refetch on debounced username change
  useEffect(() => {
    if (debouncedUsername && debouncedUsername.length >= 2) {
      fetchAgain();
    }
  }, [debouncedUsername, fetchAgain]);

  // ** Display username suggestions
  const displaySuggestions = () => {
    if (isLoadingSuggestions) {
      return (
        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
          {[1, 2, 3, 4, 5].map((_, index) => (
            <Box pt={1} key={index}>
              <Skeleton sx={{ borderRadius: "10px" }} width={80} height={40} />
            </Box>
          ))}
        </Stack>
      );
    } else if (userNameSuggestion?.length > 0) {
      return (
        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
          {userNameSuggestion.map((suggestion, index) => (
            <Box pt={1} key={index}>
              <Chip
                label={`@${suggestion}`}
                onClick={() => {
                  setValue("userName", suggestion);
                  setUsername(suggestion);
                }}
                clickable
                sx={{
                  backgroundColor: "rgba(108, 0, 230, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(108, 0, 230, 0.2)",
                  },
                }}
              />
            </Box>
          ))}
        </Stack>
      );
    }
    return null;
  };

  /**
   * Handle Manual Registration
   * Creates account and moves to next step (does NOT redirect to home)
   */
  const onSubmit = async (data) => {
    try {
      const payload = {
        user_name: data.userName,
        userName: {
          2: data.email,
        },
        fullName: data.fullName,
        password: data.password,
        confirmPassword: data.confirmPassword,
        device: 3,
        terms: 1,
      };

      const completeRegistration = await dispatch(registerAction(payload));

      if (completeRegistration.type === "auth/register/fulfilled") {
        // Store user type
        localStorage.setItem("type", "user");
        
        const userId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
        const userData = { userId: userId };
        localStorage.setItem("userData", JSON.stringify(userData));

        enqueueSnackbar("account created successfully!", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 3000,
        });

        // ✅ Move to next step instead of redirecting to home
        onComplete({
          userId: userId,
          email: data.email,
          fullName: data.fullName,
          userName: data.userName,
        });
      }
    } catch (error) {
      console.error("Registration error:", error);
      enqueueSnackbar("registration failed. please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 3000,
      });
    }
  };

  /**
   * Handle Google SSO Registration
   * After successful Google auth, moves to next step (does NOT redirect to home)
   */
  const signInWithGoogle = async () => {
    try {
      const googleProvider = new GoogleAuthProvider();
      googleProvider.addScope("https://www.googleapis.com/auth/userinfo.email");

      const result = await signInWithPopup(authentication, googleProvider);

      const payload = {
        userName: { 4: result.user.uid },
        fullName: result.user.displayName,
        device: 3,
        ...(result?.user?.email && {
          userEmail: result.user.providerData[0].email,
        }),
      };

      const completeGoogleLogin = await dispatch(login(payload));

      if (completeGoogleLogin.type === "auth/login/fulfilled") {
        localStorage.setItem("type", "user");

        const userId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
        const userData = { userId: userId };
        localStorage.setItem("userData", JSON.stringify(userData));

        if (completeGoogleLogin.payload?.accessToken) {
          localStorage.setItem(authConfig.storageTokenKeyName, completeGoogleLogin.payload.accessToken);
        }

        enqueueSnackbar("account created successfully!", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 2000,
        });

        // ✅ Move to next step instead of redirecting to home
        onComplete({
          userId: userId,
          email: result.user.email,
          fullName: result.user.displayName,
        });
      }
    } catch (error) {
      console.error("Google registration error:", error);
      enqueueSnackbar("google registration failed. please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
    }
  };

  /**
   * Handle Apple SSO Registration
   * Uses Firebase Apple OAuth provider
   */
  const signInWithApple = async () => {
    try {
      const appleProvider = new OAuthProvider('apple.com');
      appleProvider.addScope('email');
      appleProvider.addScope('name');

      const result = await signInWithPopup(authentication, appleProvider);

      const payload = {
        userName: { 6: result.user.uid }, // Type 6 for Apple OAuth
        fullName: result.user.displayName || 'Apple User',
        device: 3,
        ...(result?.user?.email && {
          userEmail: result.user.providerData[0].email,
        }),
      };

      const completeAppleLogin = await dispatch(login(payload));

      if (completeAppleLogin.type === "auth/login/fulfilled") {
        localStorage.setItem("type", "user");

        const userId = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
        const userData = { userId: userId };
        localStorage.setItem("userData", JSON.stringify(userData));

        if (completeAppleLogin.payload?.accessToken) {
          localStorage.setItem(authConfig.storageTokenKeyName, completeAppleLogin.payload.accessToken);
        }

        enqueueSnackbar("account created successfully!", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          autoHideDuration: 2000,
        });

        // ✅ Move to next step instead of redirecting to home
        onComplete({
          userId: userId,
          email: result.user.email,
          fullName: result.user.displayName || 'Apple User',
        });
      }
    } catch (error) {
      console.error("Apple registration error:", error);
      enqueueSnackbar("apple registration failed. please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        autoHideDuration: 2000,
      });
    }
  };

  return (
    <div className="register-step-account">
      <div className="register-step-account__card" style={{ position: 'relative' }}>
        {/* Loading Overlay - Shows during account creation */}
        <LoadingOverlay 
          isVisible={loadingBtnLoader} 
          type="account"
        />
        
        {/* Title */}
        <h2 className="register-step-account__title">create an account</h2>

        {/* Social Registration Buttons */}
        <div className="register-step-account__social">
          {/* Google Button */}
          <button
            type="button"
            className="register-step-account__social-btn"
            onClick={signInWithGoogle}
            disabled={loadingBtnLoader}
          >
            <span className="register-step-account__social-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36362H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z" fill="#4285F4"/>
                <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z" fill="#34A853"/>
                <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
              </svg>
            </span>
            <span className="register-step-account__social-text">continue with google</span>
          </button>

          {/* Apple Button */}
          <button
            type="button"
            className="register-step-account__social-btn"
            onClick={signInWithApple}
            disabled={loadingBtnLoader}
          >
            <span className="register-step-account__social-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.4 9.6C14.4 7.2 16.2 6 16.2 6C15 4.2 13.2 4.2 12.6 4.2C11.1 4.1 9.6 5.1 8.9 5.1C8.1 5.1 6.9 4.2 5.7 4.2C3.6 4.3 1.2 6.3 1.2 10.2C1.2 11.7 1.5 13.2 2.1 14.7C2.7 16.2 4.8 19.8 6.6 19.8C7.8 19.8 8.4 19.2 9.9 19.2C11.4 19.2 11.7 19.8 13.2 19.8C15 19.8 16.8 16.5 17.4 15C17.4 15 14.4 13.5 14.4 9.6ZM12 2.7C13.2 1.2 12.9 0 12.9 0C11.7 0.1 10.5 0.9 9.9 1.8C9.3 2.7 9 3.6 9.1 4.5C10.2 4.6 11.1 3.9 12 2.7Z" fill="#000000"/>
              </svg>
            </span>
            <span className="register-step-account__social-text">continue with apple</span>
          </button>
        </div>

        {/* Divider */}
        <div className="register-step-account__divider">
          <span className="register-step-account__divider-line"></span>
          <span className="register-step-account__divider-text">or</span>
          <span className="register-step-account__divider-line"></span>
        </div>

        {/* Manual Registration Form */}
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="register-step-account__form">
          
          {/* Full Name */}
          <InputField
            {...register("fullName")}
            type="text"
            placeholder="enter full name*"
            value={formValues.fullName}
            error={errors.fullName ? errors.fullName.message : ''}
            required
          />

          {/* Username with @ prefix */}
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888',
                fontSize: '16px',
                fontWeight: 500,
                zIndex: 1,
              }}>
                @
              </span>
              <InputField
                {...register("userName")}
                type="text"
                placeholder="username*"
                value={username}
                onChange={handleUsernameChange}
                error={errors.userName ? errors.userName.message : ''}
                required
                style={{ paddingLeft: '32px' }}
              />
            </div>
            {displaySuggestions()}
          </div>

          {/* Email */}
          <InputField
            {...register("email")}
            type="email"
            placeholder="enter email address*"
            value={formValues.email}
            error={errors.email ? errors.email.message : ''}
            required
          />

          {/* Password */}
          <div className="register-step-account__password-wrapper">
            <InputField
              {...register("password")}
              type={showPassword ? "text" : "password"}
              placeholder="create password*"
              value={formValues.password}
              error={errors.password ? errors.password.message : ''}
              required
            />
            <button
              type="button"
              className="register-step-account__password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label="toggle password visibility"
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>

          {/* Confirm Password */}
          <div className="register-step-account__password-wrapper">
            <InputField
              {...register("confirmPassword")}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="confirm password*"
              value={formValues.confirmPassword}
              error={errors.confirmPassword ? errors.confirmPassword.message : ''}
              required
            />
            <button
              type="button"
              className="register-step-account__password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label="toggle confirm password visibility"
            >
              {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>

          {/* Forgot Password Link */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px', marginBottom: '4px' }}>
            <button
              type="button"
              onClick={() => navigate('/account/password-reset-request')}
              disabled={loadingBtnLoader}
              className="login-page-new__signup-link-text"
              style={{
                background: 'none',
                border: 'none',
                cursor: loadingBtnLoader ? 'not-allowed' : 'pointer',
                opacity: loadingBtnLoader ? 0.6 : 1,
                padding: 0,
              }}
            >
              forgot password?
            </button>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="register-step-account__submit-btn"
            disabled={loadingBtnLoader}
          >
            {loadingBtnLoader ? 'creating account...' : 'continue'}
          </button>
        </form>

        {/* Login Link */}
        <div className="register-step-account__login-link">
          <span className="register-step-account__login-text">already have an account? </span>
          <a href="/business/login" className="register-step-account__login-link-text">
            login
          </a>
        </div>
      </div>
    </div>
  );
};

export default RegisterStepAccount;


